import { Controller } from '@hotwired/stimulus'

export default class MainMenuController extends Controller {
  static targets = ['trigger', 'submenu', 'item']

  declare readonly triggerTargets: HTMLAnchorElement[]
  declare readonly submenuTargets: HTMLElement[]
  declare readonly itemTargets: HTMLElement[]

  declare readonly hasTriggerTarget: boolean
  declare readonly hasSubmenuTarget: boolean
  declare readonly hasItemTarget: boolean

  private closeTimer: ReturnType<typeof setTimeout> | null = null

  // ── Lifecycle ────────────────────────────────────────────────────

  connect(): void {
    this.itemTargets.forEach((item) => {
      item.addEventListener('mouseenter', this.onMouseEnter)
      item.addEventListener('mouseleave', this.onMouseLeave)
    })

    this.triggerTargets.forEach((trigger) => {
      trigger.addEventListener('keydown', this.onTriggerKeydown)
    })

    document.addEventListener('keydown', this.onDocumentKeydown)
  }

  disconnect(): void {
    this.itemTargets.forEach((item) => {
      item.removeEventListener('mouseenter', this.onMouseEnter)
      item.removeEventListener('mouseleave', this.onMouseLeave)
    })

    this.triggerTargets.forEach((trigger) => {
      trigger.removeEventListener('keydown', this.onTriggerKeydown)
    })

    document.removeEventListener('keydown', this.onDocumentKeydown)
    this.clearCloseTimer()
  }

  // ── Mouse ────────────────────────────────────────────────────────

  private onMouseEnter = (event: Event): void => {
    // Cancel any pending close so moving into the submenu keeps it open.
    this.clearCloseTimer()

    const item = event.currentTarget as HTMLElement
    // Close any other open item first.
    this.closeAll(item)
    this.open(item)
  }

  private onMouseLeave = (event: Event): void => {
    const item = event.currentTarget as HTMLElement

    // Small delay: if the cursor moves to a sibling item, onMouseEnter fires
    // before the timer expires and cancels it — no flicker.
    this.closeTimer = setTimeout(() => {
      this.close(item)
    }, 100)
  }

  // ── Keyboard ────────────────────────────────────────────────────

  private onTriggerKeydown = (event: KeyboardEvent): void => {
    const trigger = event.currentTarget as HTMLAnchorElement
    const item = trigger.closest<HTMLElement>('[data-main-menu-target="item"]')
    if (!item) return

    const isOpen = item.hasAttribute('data-open')

    switch (event.key) {
      case 'Enter':
      case ' ': {
        event.preventDefault()
        if (isOpen) {
          this.close(item)
        } else {
          this.closeAll()
          this.open(item)
          trigger.setAttribute('data-keyboard-open', '')
          const submenu = this.getSubmenu(item)
          submenu?.querySelector<HTMLElement>('a')?.focus()
        }
        break
      }

      case 'Escape': {
        if (isOpen) {
          event.stopPropagation()
          this.close(item)
          trigger.focus()
        }
        break
      }

      case 'ArrowDown': {
        if (!isOpen) {
          event.preventDefault()
          this.closeAll()
          this.open(item)
          trigger.setAttribute('data-keyboard-open', '')
          const submenu = this.getSubmenu(item)
          submenu?.querySelector<HTMLElement>('a')?.focus()
        }
        break
      }
    }
  }

  // Close all panels when pressing Escape anywhere in the document.
  private onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.closeAll()
    }
  }

  // ── Open / close helpers ────────────────────────────────────────

  /**
   * Sets [data-open] on the <li> — CSS handles all visual transitions.
   * Also syncs aria-expanded on the trigger.
   */
  private open(item: HTMLElement): void {
    item.setAttribute('data-open', '')
    const trigger = this.getTrigger(item)
    trigger?.setAttribute('aria-expanded', 'true')
    this.syncNavState()
  }

  private close(item: HTMLElement): void {
    item.removeAttribute('data-open')
    const trigger = this.getTrigger(item)
    trigger?.setAttribute('aria-expanded', 'false')
    trigger?.removeAttribute('data-keyboard-open')
    this.syncNavState()
  }

  /** Close all items, optionally skipping one (e.g. the one just entered). */
  private closeAll(except?: HTMLElement): void {
    this.itemTargets.forEach((item) => {
      if (item !== except) this.close(item)
    })
    this.syncNavState()
  }

  // ── Nav state ──────────────────────────────────────────────────

  /**
   * Reflects whether any submenu is open onto the <nav> element.
   * Tailwind's group-data-[has-open]/nav variant reads this attribute
   * to dim inactive triggers.
   */
  private syncNavState(): void {
    const anyOpen = this.itemTargets.some((item) =>
      item.hasAttribute('data-open'),
    )
    if (anyOpen) {
      this.element.setAttribute('data-has-open', '')
    } else {
      this.element.removeAttribute('data-has-open')
    }
  }

  // ── DOM helpers ────────────────────────────────────────────────

  private getTrigger(item: HTMLElement): HTMLAnchorElement | null {
    return item.querySelector<HTMLAnchorElement>(
      '[data-main-menu-target="trigger"]',
    )
  }
  private getSubmenu(item: HTMLElement): HTMLElement | null {
    return item.querySelector<HTMLElement>('[data-main-menu-target="submenu"]')
  }

  private clearCloseTimer(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }
}
